// ** Modules
import { motion } from 'framer-motion';

// ** UI
import Table from '@mui/material/Table';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';

// ** Styles
import { DIN } from '@/styles/globalStyles';
import { axisValueSx } from './styles';

// ** Types
import type { GraphTableData } from './types';

const MotionTableRow = motion.create(TableRow);

type Props = { data: GraphTableData };

const STRIPE_BG = '#F4F3F2';
const HOVER_BG = '#E8E7E6';

const stickyCell = {
	position: 'sticky',
	left: 0,
	zIndex: 1
} as const;

const tableHeaderSx = {
	color: 'common.black',
	fontFamily: DIN,
	fontStyle: 'normal',
	fontWeight: 500,
	letterSpacing: '-0.05px',
	fontSize: { xs: '10px', sm: '18px' },
	lineHeight: { xs: '16px', sm: '28px' },
	border: 'none'
};

export default function TableView({ data }: Props) {
	return (
		<TableContainer
			sx={{
				overflowX: 'auto',
				scrollbarWidth: 'none',
				'&::-webkit-scrollbar': { display: 'none' },
				px: 0,
				width: { xs: '100vw', md: '100%' },
				marginLeft: { xs: 'calc(50% - 50vw)', md: 0 },
				marginRight: { xs: 'calc(50% - 50vw)', md: 0 }
			}}
		>
			<Table
				size="small"
				sx={{
					minWidth: 720,
					// Body rows: fixed 32px on xs so content doesn't push to 40–42px (24px line-height + padding)
					'& tbody .MuiTableCell-root': {
						border: 'none',
						height: { xs: 32, sm: '66px' },
						minHeight: { xs: 32, sm: 'unset' },
						py: { xs: 2, sm: 4 },
						px: 0
					},
					// Left column: 16px padding on mobile so header/year sit 16px from screen (row bg full-bleed); from md+ align with page gutters
					'& tr > .MuiTableCell-root:first-of-type': {
						pl: { xs: 4, md: 10, lg: 20 }
					},
					// Right column: on mobile match outer padding so content doesn’t touch screen edge
					'& tr > .MuiTableCell-root:last-of-type': {
						pr: { xs: 5, sm: 0 }
					},
					// Header spacing to the bottom border:
					// xs: 8px, md+: 16px (increase on desktop below)
					'& thead .MuiTableCell-root': {
						border: 'none',
						py: 0,
						pb: { xs: 1, md: 2.5 }
					},
					// Sticky "Jahr" header cell + divider (acts as sticky limit)
					'& thead tr > .MuiTableCell-root:first-of-type': {
						position: { xs: 'sticky', md: 'static' },
						left: { xs: 0, md: 'auto' },
						zIndex: { xs: 10, md: 'auto' },
						backgroundColor: { xs: 'common.white', md: 'transparent' },
						// Make sure the divider is visible above scrolled header cells
						isolation: 'isolate',
						'&::after': {
							content: '""',
							position: 'absolute',
							top: 0,
							bottom: 8, // keep 8px gap to the horizontal border
							right: 0,
							width: '1px',
							backgroundColor: '#D9D9D9',
							zIndex: 1,
							display: { xs: 'block', md: 'none' },
							pointerEvents: 'none'
						}
					}
				}}
			>
				<TableHead>
					<TableRow
						sx={{
							borderBottom: '1px solid #D9D9D9'
						}}
					>
						<TableCell
							sx={{
								...tableHeaderSx,
								...stickyCell,
								bgcolor: 'common.white',
								zIndex: 2,
								pr: { xs: 1, sm: 2 }
							}}
						>
							{data.xAxisTitle}
						</TableCell>
						{data.series.map((s) => (
							<TableCell key={s.name} align="center" sx={tableHeaderSx}>
								{s.name}
							</TableCell>
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					{data.categories.map((cat, idx) => {
						const rowBg = idx % 2 === 1 ? STRIPE_BG : 'common.white';
						return (
							<MotionTableRow
								key={cat}
								initial={{ opacity: 0, y: 10 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{
									duration: 0.2,
									ease: [0, 0, 0.2, 1],
									delay: idx * 0.03
								}}
								sx={{ bgcolor: rowBg, '&:hover td': { bgcolor: HOVER_BG } }}
							>
								<TableCell
									sx={{
										...axisValueSx,
										...stickyCell,
										bgcolor: rowBg,
										pr: { xs: 1, sm: 2 },
										'tr:hover &': { bgcolor: HOVER_BG }
									}}
								>
									{cat}
								</TableCell>
								{data.series.map((s) => (
									<TableCell key={`${cat}-${s.name}`} align="center" sx={axisValueSx}>
										{s.data[idx] ?? ''}
									</TableCell>
								))}
							</MotionTableRow>
						);
					})}
				</TableBody>
			</Table>
		</TableContainer>
	);
}
